import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import type { Domain } from '@/types/domain';

export interface IssueCertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Domains eligible for issuance (DNS validated, assigned to a node) */
  domains: Domain[];
  onIssue: (domainId: string) => Promise<unknown>;
}

/**
 * Issue a Let's Encrypt certificate for a domain.
 * Mount with a `key` (or only while open) to reset the selection.
 */
export function IssueCertificateModal({ isOpen, onClose, domains, onIssue }: IssueCertificateModalProps) {
  const [domainId, setDomainId] = useState('');
  const issue = useAsyncAction(onIssue, { errorMessage: 'Failed to issue certificate' });
  const isIssuing = issue.isPending();

  const handleIssue = async () => {
    if (!domainId) return;
    const result = await issue.run(domainId);
    if (result.ok) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Issue New Certificate</ModalHeader>
      <ModalBody>
        <p className="certs-modal-description">
          Select a domain to issue a new TLS certificate via Let's Encrypt.
          The domain must have DNS validated and be assigned to a node.
        </p>

        <div className="certs-modal-field">
          <label htmlFor="issue-domain-select" className="certs-modal-label">
            Domain *
          </label>
          <Select
            id="issue-domain-select"
            value={domainId}
            onChange={setDomainId}
            placeholder="Select a domain..."
            options={domains.map((domain) => ({ value: domain.id, label: domain.name }))}
            className="certs-modal-select"
            required
          />
          {domains.length === 0 && (
            <p className="certs-modal-hint certs-modal-hint-warning">
              No eligible domains found. Domains must have DNS validated and be assigned to a node.
            </p>
          )}
        </div>

        {issue.error && (
          <div className="modal-error" role="alert">
            {issue.error}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={isIssuing}>
          Cancel
        </Button>
        <Button onClick={handleIssue} isLoading={isIssuing} disabled={!domainId || isIssuing}>
          Issue Certificate
        </Button>
      </ModalFooter>
    </Modal>
  );
}
